import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getSystemConfig,
  patchSystemConfig,
  reloadSystemConfig,
  testEmailSetting,
  testModelSetting,
  getApiKeyStatus,
  SystemConfigRow,
  ApiKeyStatus,
} from '@/api/client';

/**
 * System Configuration panel — admin-only. Renders every DB-backed
 * config key grouped by category, with per-field Save + Test buttons.
 * Uses native HTML (matching the Login screen) to sidestep the
 * untyped forwardRef shadcn wrappers.
 */

type CategoryMap = Record<string, SystemConfigRow[]>;

const CATEGORY_LABELS: Record<string, string> = {
  models: 'Models',
  pageindex: 'PageIndex',
  email: 'Email',
  auth: 'Auth & Sessions',
  storage: 'Storage',
  system: 'System',
};

const CATEGORY_ORDER = ['models', 'pageindex', 'email', 'auth', 'storage', 'system'];

// Model + provider keys that are now edited exclusively through
// ModelConfigTable in Settings.tsx. Hidden from the Models tab in
// this full-coverage admin panel so drafters don't see two
// separate editors for the same row. The keys are otherwise
// functional — they're still in system_config and still routed by
// callLLM — only the listing here suppresses them.
const MODELS_TAB_HIDDEN_KEYS: ReadonlySet<string> = new Set([
  'default_draft_model',      'default_draft_provider',
  'default_fast_model',       'default_fast_provider',
  'fast_fallback_model',      'fast_fallback_provider',
  'vision_model',             'vision_provider',
  'tree_model',               'tree_provider',
  'retrieval_model',          'retrieval_provider',
  'qa_model',                 'qa_provider',
  'qc_model',                 'qc_provider',
  'provenance_model',         'provenance_provider',
  'provenance_fallback_model',
]);

const inputClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 ' +
  'text-sm ring-offset-background placeholder:text-muted-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

const primaryBtn =
  'inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm ' +
  'font-medium text-primary-foreground transition-colors hover:bg-primary/90 ' +
  'disabled:opacity-50 disabled:pointer-events-none';

const outlineBtn =
  'inline-flex h-9 items-center justify-center rounded-md border border-input ' +
  'bg-background px-3 text-sm font-medium text-foreground hover:bg-accent ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

function Field({
  row,
  onSave,
  onTest,
}: {
  row: SystemConfigRow;
  onSave: (key: string, value: string) => Promise<string | null>;
  onTest?: (row: SystemConfigRow) => Promise<string>;
}) {
  const [value, setValue] = useState<string>(row.value || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    setValue(row.value || '');
  }, [row.value]);

  const save = useCallback(async () => {
    setSaving(true);
    setSaveErr(null);
    try {
      const err = await onSave(row.key, value);
      if (err) {
        setSaveErr(err);
      } else {
        setSavedAt(Date.now());
      }
    } catch (e: any) {
      setSaveErr(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [row.key, value, onSave]);

  const runTest = useCallback(async () => {
    if (!onTest) return;
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await onTest(row));
    } catch (e: any) {
      setTestResult(`error: ${e?.message || e}`);
    } finally {
      setTesting(false);
    }
  }, [row, onTest]);

  const isBool = row.type === 'boolean';
  const displayValue = (() => {
    if (row.is_sensitive || row.type === 'secret') {
      // Value from the server arrives as '••••••' when set, '' when
      // unset. Keep whatever the user is currently typing so the save
      // button works; otherwise show the masked placeholder.
      return showSecret || value === '' ? value : '••••••';
    }
    return value;
  })();

  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4 space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-medium text-zinc-900">{row.label}</div>
          {row.description && (
            <div className="text-xs text-zinc-500 mt-0.5">{row.description}</div>
          )}
          <div className="text-[11px] font-mono text-zinc-400 mt-1">
            {row.key} · type:{row.type} · source:{row.source}
          </div>
        </div>
        <div className="flex-shrink-0 text-right text-[11px] text-zinc-400">
          {row.updated_at ? (
            <>
              Last updated
              <br />
              {new Date(row.updated_at).toLocaleString()}
            </>
          ) : (
            <span className="italic">never edited</span>
          )}
        </div>
      </div>

      <div className="flex gap-2 items-center">
        {isBool ? (
          <select
            className={inputClass}
            value={String(value).toLowerCase() === 'true' || value === '1' ? 'true' : 'false'}
            onChange={(e) => setValue(e.target.value)}
            disabled={saving}
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : (
          <input
            className={inputClass}
            type={row.is_sensitive && !showSecret ? 'password' : 'text'}
            inputMode={row.type === 'integer' ? 'numeric' : 'text'}
            value={displayValue === '••••••' ? '' : value}
            placeholder={displayValue === '••••••' ? '•••••• (currently set)' : row.default_value || ''}
            onChange={(e) => setValue(e.target.value)}
            disabled={saving}
          />
        )}
        {(row.is_sensitive || row.type === 'secret') && (
          <button
            type="button"
            className={outlineBtn}
            onClick={() => setShowSecret((s) => !s)}
            disabled={saving}
            title={showSecret ? 'Hide' : 'Show'}
          >
            {showSecret ? 'Hide' : 'Show'}
          </button>
        )}
        <button type="button" className={primaryBtn} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {onTest && (
          <button type="button" className={outlineBtn} onClick={runTest} disabled={testing}>
            {testing ? 'Testing…' : 'Test'}
          </button>
        )}
      </div>
      {saveErr && <div className="text-xs text-red-600">{saveErr}</div>}
      {savedAt && !saveErr && (
        <div className="text-xs text-emerald-600">
          Saved {new Date(savedAt).toLocaleTimeString()}
        </div>
      )}
      {testResult && (
        <div className="text-xs font-mono text-zinc-700 whitespace-pre-wrap">{testResult}</div>
      )}
    </div>
  );
}

/**
 * ApiKeyStatusChips — read-only row of provider chips showing which
 * direct-API keys are configured in the process environment. Powers
 * the header of the Models tab so admins can see at a glance whether
 * an Anthropic / OpenAI / Google / OpenRouter key is available
 * before flipping a tier's {tier}_provider row to that provider.
 *
 * Green = configured; amber = missing. Fetches once on mount; the
 * keys are env-only and don't change at runtime. Silently renders
 * nothing on fetch failure so a missing endpoint can't break the
 * Settings page.
 */
function ApiKeyStatusChips() {
  const [status, setStatus] = React.useState<ApiKeyStatus | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getApiKeyStatus();
        if (!cancelled) setStatus(s);
      } catch {
        if (!cancelled) setStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!status) return null;
  const chips: Array<{ label: string; ok: boolean; source: 'db' | 'env' | null }> = [
    { label: 'Anthropic',  ok: !!status.anthropic?.configured,  source: status.anthropic?.source  ?? null },
    { label: 'OpenAI',     ok: !!status.openai?.configured,     source: status.openai?.source     ?? null },
    { label: 'Google',     ok: !!status.google?.configured,     source: status.google?.source     ?? null },
    { label: 'OpenRouter', ok: !!status.openrouter?.configured, source: status.openrouter?.source ?? null },
    { label: 'Fireworks',  ok: !!status.fireworks?.configured,  source: status.fireworks?.source  ?? null },
  ];
  return (
    <div
      data-testid="api-keys-status"
      className="flex items-center gap-2 flex-wrap text-[11px] mb-2"
    >
      <span className="font-mono uppercase tracking-widest text-zinc-500">API Keys:</span>
      {chips.map((c) => (
        <span
          key={c.label}
          data-testid={`api-key-chip-${c.label.toLowerCase()}`}
          title={
            c.ok
              ? `${c.label} API key is set (source: ${c.source || 'unknown'})`
              : `${c.label} API key is not set — add it in the Settings page or as ${c.label.toUpperCase()}_API_KEY in the environment`
          }
          className={
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium '
            + (c.ok
              ? 'bg-green-50 text-green-800 border-green-200'
              : 'bg-amber-50 text-amber-800 border-amber-200')
          }
        >
          {c.ok ? '✅' : '⚠'} {c.label}
          {c.ok && c.source && (
            <span className="text-[9px] font-mono text-green-700/80 uppercase tracking-wider">
              ({c.source})
            </span>
          )}
        </span>
      ))}
    </div>
  );
}


export default function SystemConfigPanel() {
  const [categories, setCategories] = useState<CategoryMap>({});
  const [total, setTotal] = useState(0);
  const [active, setActive] = useState<string>('models');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await getSystemConfig();
      setCategories(data.categories || {});
      setTotal(data.total_keys || 0);
      if (!(active in (data.categories || {}))) {
        const firstAvailable = CATEGORY_ORDER.find((c) => c in (data.categories || {}));
        if (firstAvailable) setActive(firstAvailable);
      }
    } catch (e: any) {
      setErr(e?.response?.status === 403 ? 'Admin access required.' : 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async (key: string, value: string) => {
    try {
      await patchSystemConfig(key, value);
      await load();
      return null;
    } catch (e: any) {
      return e?.response?.data?.detail || 'Save failed';
    }
  }, [load]);

  const test = useCallback(async (row: SystemConfigRow): Promise<string> => {
    if (row.category === 'models') {
      const r = await testModelSetting(row.key);
      // Tier-chain keys (capable_chain / fast_chain) return a per-entry
      // breakdown: every entry is probed individually so a dead
      // fallback is visible without masking a healthy primary.
      if (Array.isArray(r.chain)) {
        const lines = r.chain.map((e, i) =>
          e.ok
            ? `  ${i + 1}. ✓ ${e.provider}/${e.model} · ${e.latency_ms}ms`
            : `  ${i + 1}. ✗ ${e.provider}/${e.model} — ${e.error || 'unknown error'}`,
        );
        const warnings = (r.warnings || []).map((w) => `  ⚠ ${w}`);
        const head = r.success ? `✓ ${r.model}` : `✗ ${r.model}`;
        return [head, ...lines, ...warnings].join('\n');
      }
      if (r.success) {
        return `✓ ${r.model} · ${r.latency_ms}ms · sample="${r.sample || ''}"`;
      }
      return `✗ ${r.error || 'unknown error'}`;
    }
    if (row.key === 'smtp_host' || row.category === 'email') {
      const r = await testEmailSetting();
      if (r.success) {
        return `✓ sent to ${r.recipient} via ${r.smtp_host} (${r.latency_ms}ms)`;
      }
      return `✗ ${r.error || 'unknown error'}`;
    }
    return 'No test available for this field.';
  }, []);

  const reloadAll = async () => {
    setReloading(true);
    try {
      await reloadSystemConfig();
      await load();
    } finally {
      setReloading(false);
    }
  };

  const tabs = useMemo(
    () => CATEGORY_ORDER.filter((c) => c in categories),
    [categories],
  );

  // The Models tab used to list every model/provider config key
  // from system_config, but those are now edited through
  // ModelConfigTable in Settings.tsx — rendering both surfaces
  // produced a confusing "two places to change the same row"
  // experience. Hide the 19 keys listed in MODELS_TAB_HIDDEN_KEYS
  // (module-level) when the Models tab is active; every other tab
  // (PageIndex / Email / Auth / Storage / System) keeps listing
  // its rows unchanged.
  const rows = (categories[active] || []).filter((row) => (
    active !== 'models' || !MODELS_TAB_HIDDEN_KEYS.has(row.key)
  ));
  const canTest = (r: SystemConfigRow) =>
    r.category === 'models' || r.key === 'smtp_host';

  if (loading) {
    return <div className="p-6 text-sm text-zinc-500">Loading settings…</div>;
  }
  if (err) {
    return <div className="p-6 text-sm text-red-600">{err}</div>;
  }

  return (
    <div data-testid="system-config-panel" className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">System Configuration</h2>
          <p className="text-xs text-zinc-500">
            {total} keys · changes take effect immediately · no restart required
          </p>
        </div>
        <button type="button" className={outlineBtn} onClick={reloadAll} disabled={reloading}>
          {reloading ? 'Reloading…' : 'Reload from DB'}
        </button>
      </div>

      <div className="flex gap-1 border-b border-zinc-200" role="tablist">
        {tabs.map((c) => (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={active === c}
            className={
              'px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ' +
              (active === c
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-zinc-500 hover:text-zinc-900')
            }
            onClick={() => setActive(c)}
          >
            {CATEGORY_LABELS[c] || c}
            <span className="ml-1 text-[11px] text-zinc-400">
              ({c === 'models'
                ? (categories[c] || []).filter((r) => !MODELS_TAB_HIDDEN_KEYS.has(r.key)).length
                : categories[c].length})
            </span>
          </button>
        ))}
      </div>

      {active === 'models' && <ApiKeyStatusChips />}

      {active === 'models' && rows.length === 0 && (
        <div
          data-testid="models-tab-redirected"
          className="text-xs text-zinc-500 border border-zinc-200 bg-zinc-50 rounded-sm p-3 leading-relaxed"
        >
          Model and provider editing moved to the main Settings page —
          look for the <span className="font-medium text-zinc-700">Model Configuration</span> table
          just above this panel. Every model / provider pair is editable there with an inline Test button.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((row) => (
          <Field
            key={row.key}
            row={row}
            onSave={save}
            onTest={canTest(row) ? test : undefined}
          />
        ))}
      </div>
    </div>
  );
}
